import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Input from "components/atoms/Input";
import Icon from "components/atoms/Icon";
import DrugSearchInput from "components/molecules/DrugSearchInput";
import {
  PrescriptionGroup,
  Prescription,
  Drug,
  EMPTY_PRESCRIPTION,
} from "common/models/types";
import { log, LogLevel } from "utils/LogUtil";
import { getDateStringFromToday } from "utils/TimeUtil";
import { COLOR_SYSTEM, Flex } from "components/atoms";
import { fetchDrugs } from "api/drugService";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import IdentifiedDrugList from "./IdentifiedDrugList";
import { createNhisRequest, fetchNhisRequestStatus } from "api/nhisService";
import { NhisStatus } from "types/nhis";
import { useFeature } from "hooks/useFeature";
import NhisProgressDashboard from "components/organisms/NhisRequestStatusList/NhisProgressDashboard";
import NhisStepPreview from "components/organisms/NhisRequestStatusList/NhisStepPreview";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0px 0px 0px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  margin-bottom: 24px;
`;

const SectionDescription = styled.p`
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 14px;
  margin-top: 8px;
`;

// [2025-01-17] 결정사항: 초기 선택 화면을 위한 스타일 컴포넌트 추가
const SelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 40px 0px;
`;

const SelectionOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  width: 100%;
  max-width: 800px;
`;

const SelectionCard = styled.div`
  flex: 1;
  min-width: 0;
  max-width: calc(50% - 12px);
  padding: 32px 24px;
  border: 2px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 12px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[300]};
    background: ${COLOR_SYSTEM.get("Blue")?.[25]};
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${COLOR_SYSTEM.get("Blue")?.[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  margin-bottom: 16px;
  align-self: flex-start;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const PrescriptionCard = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const PrescriptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const UploadButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  align-items: flex-end;

  a {
    text-decoration: none;
    color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  }
`;

const MedicineTable = styled.table`
  overflow-x: auto;
  // border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  background: #fff;

  /* Input 컴포넌트의 기본 min-width 오버라이드 */
  .standard-code,
  .medication-name,
  .dosage,
  .frequency,
  .duration,
  .usage-instruction,
  .item-code {
    & > div {
      min-width: unset !important;
    }
  }

  /* 툴팁 호버 효과 */
  .item-code > div > div:hover .tooltip {
    opacity: 1 !important;
  }

  .standard-code {
    width: 160px;
    max-width: 160px;
    padding-bottom: 6px;
  }
  .medication-name {
    width: 300px;
    max-width: 300px;
    padding-bottom: 6px;
  }
  .dosage {
    width: 80px;
    max-width: 80px;
    padding-bottom: 6px;
  }
  .frequency {
    width: 80px;
    max-width: 80px;
    padding-bottom: 6px;
  }
  .duration {
    width: 100px;
    max-width: 100px;
    padding-bottom: 6px;
  }
  .usage-instruction {
    width: 140px;
    max-width: 140px;
    padding-bottom: 6px;
  }
  .item-code {
    width: 140px;
    max-width: 140px;
    padding-bottom: 6px;
  }
  .delete-button {
    width: 40px;
    max-width: 40px;
    padding-bottom: 6px;
  }
`;

const TableHeader = styled.tr`
  // display: grid;
  // grid-template-columns: 1.5fr 2.5fr 1fr 1fr 1fr 1.5fr 1.5fr 0.7fr;
  // gap: 8px;
  // background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  // padding: 12px 8px;
  // border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  // font-weight: 600;
`;

const TableRow = styled.tr`
  // display: grid;
  // grid-template-columns: 1.5fr 2.5fr 1fr 1fr 1fr 1.5fr 1.5fr 0.7fr;
  // gap: 8px;
  // padding: 12px 8px;
  align-items: center;
  // border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  background: #fff;
`;

const NoteBox = styled.div`
  // background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  padding: 12px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const ButtonContainer = styled.div`
  // position: absolute;
  // top: 16px;
  // right: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

// [2025-01-02] 식별약 패널 관련 스타일 컴포넌트
const ContentWrapper = styled.div<{ $showPanel: boolean }>`
  display: flex;
  gap: 24px;
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0; /* 테이블 오버플로우 방지 */
`;

const SidePanel = styled.div`
  width: 320px;
  flex-shrink: 0;
  border-left: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding-left: 24px;
`;

// [2025-01-17] 결정사항: 입력 모드 타입 정의 - 선택, 엑셀 업로드, 직접 입력을 구분
type InputMode = "selection" | "excel" | "manual" | "nhis";

interface PrescriptionFormTemplateProps {
  title?: string;
  initialGroup?: PrescriptionGroup;
  existingHospitalNames?: string[];
  onSubmit: (group: PrescriptionGroup) => Promise<void>;
  onCancel: () => void;
  onExcelUploadClick?: () => void;
  onPictureUploadClick?: () => void;
}

const PrescriptionFormTemplate: React.FC<PrescriptionFormTemplateProps> = ({
  title,
  initialGroup,
  existingHospitalNames,
  onSubmit,
  onCancel,
  onExcelUploadClick,
  onPictureUploadClick,
}) => {
  const isNhisEnabled = useFeature('NHIS');

  // [2025-06-26] 결정사항: 수정 모드(기존 처방전 그룹이 있는 경우)는 바로 직접 입력 화면으로, 새 추가는 선택 화면부터 시작
  const isEditMode = initialGroup && initialGroup.groupId;
  const [inputMode, setInputMode] = useState<InputMode>(isEditMode ? "manual" : "selection");
  const addMedicineButtonRef = useRef<HTMLDivElement>(null);

  // [2025-01-02] 식별약 패널 표시 상태
  const [showIdentifiedDrugs, setShowIdentifiedDrugs] = useState(false);

  const [group, setGroup] = useState<PrescriptionGroup>(() => ({
    groupId: initialGroup?.groupId || crypto.randomUUID(),
    prescriptionDate: initialGroup?.prescriptionDate || new Date(),
    hospital: initialGroup?.hospital || "",
    prescriptions: initialGroup?.prescriptions || [],
    patientId: initialGroup?.patientId,
    deletedPrescriptionIds: initialGroup?.deletedPrescriptionIds || [],
  }));

  const handleMedicineChange = async (
    prescriptionIndex: number,
    medicineIndex: number,
    field: keyof Prescription,
    value: string
  ) => {
    setGroup((prev) => {
      const newPrescriptions = [...(prev.prescriptions || [])];
      const updatedPrescription = {
        ...newPrescriptions[prescriptionIndex],
        [field]: value,
      };
      
      // 의약품명이나 표준코드(보험코드)가 변경되면 품목기준코드 초기화
      if (field === 'medicationName' || field === 'standardCode') {
        updatedPrescription.itemCode = '';
        log(LogLevel.INFO, "[PrescriptionFormTemplate] 의약품 정보 수정으로 품목기준코드 초기화:", {
          field,
          value,
          prescriptionIndex,
        });
      }
      
      newPrescriptions[prescriptionIndex] = updatedPrescription;
      return { ...prev, prescriptions: newPrescriptions };
    });
  };

  // 엔터키로 다음 필드로 이동하는 함수
  const handleEnterKeyPress = (prescriptionIndex: number, currentField: string) => {
    const fieldOrder = [
      'dosage',
      'frequency', 
      'duration',
      'usage'
    ];

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const totalPrescriptions = group.prescriptions?.length || 0;
    
    if (currentField === 'usage') {
      // 마지막 처방전의 용법칸에서 엔터키를 누르면 '약물 추가' 버튼으로 이동
      if (prescriptionIndex === totalPrescriptions - 1) {
        if (addMedicineButtonRef.current) {
          // setTimeout으로 다음 틱에 포커스를 주어 엔터키 이벤트가 버블링되지 않도록 함
          setTimeout(() => {
            const button = addMedicineButtonRef.current?.querySelector('button');
            if (button) {
              button.focus();
            }
          }, 0);
        }
      } else {
        // 다음 줄의 투약량으로 이동
        const nextRowDosageInput = document.getElementById(`dosage-input-${prescriptionIndex + 1}`);
        if (nextRowDosageInput) {
          (nextRowDosageInput as HTMLInputElement).focus();
        }
      }
    } else if (currentFieldIndex !== -1 && currentFieldIndex < fieldOrder.length - 1) {
      // 다음 필드로 포커스 이동
      const nextField = fieldOrder[currentFieldIndex + 1];
      const nextInput = document.getElementById(`${nextField}-input-${prescriptionIndex}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }
    }
  };

  const handleDrugSelect = (prescriptionIndex: number, drug: Drug) => {
    setGroup((prev) => {
      const newPrescriptions = [...(prev.prescriptions || [])];
      newPrescriptions[prescriptionIndex] = {
        ...newPrescriptions[prescriptionIndex],
        standardCode: drug.standardCode || "",
        medicationName: drug.productName || "",
        itemCode: drug.itemCode || "",
        insuranceCode: drug.insuranceCode || "",
        dosageUnit: drug.dosageUnit || "",
        source: "manual",
      };
      return { ...prev, prescriptions: newPrescriptions };
    });

    // 약품 선택 후 해당 줄의 투약량 입력란으로 포커스 이동
    setTimeout(() => {
      const dosageInput = document.getElementById(`dosage-input-${prescriptionIndex}`);
      if (dosageInput) {
        dosageInput.focus();
        // 선택된 텍스트가 있다면 전체 선택 (선택사항)
        if (dosageInput instanceof HTMLInputElement) {
          dosageInput.select();
        }
      }
    }, 100);
  };

  const handleAddMedicine = () => {
    const newPrescriptions = [...(group.prescriptions || []), { ...EMPTY_PRESCRIPTION }];
    setGroup({ ...group, prescriptions: newPrescriptions });

    // 새로운 항목 추가 후 해당 DrugSearchInput으로 포커스 이동
    setTimeout(() => {
      const newIndex = newPrescriptions?.length - 1;
      const newDrugSearchInput = document.getElementById(`standard-code-input-${newIndex}`);
      if (newDrugSearchInput) {
        newDrugSearchInput.focus();
        log(LogLevel.INFO, "새로운 약물 항목 추가 후 포커스 이동:", {
          newIndex,
          elementId: `standard-code-input-${newIndex}`,
        });
      }
    }, 100);
  };

  const handleRemoveMedicine = (prescriptionIndex: number) => {
    setGroup((prev) => {
      const newPrescriptions = [...(prev.prescriptions || [])];
      const prescription = newPrescriptions[prescriptionIndex];
      if (prescription.id) {
        // 이미 저장된 처방약인 경우 삭제 목록에 추가
        newPrescriptions.splice(prescriptionIndex, 1);
        return {
          ...prev,
          prescriptions: newPrescriptions,
          deletedPrescriptionIds: [...(prev.deletedPrescriptionIds || []), prescription.id],
        };
      } else {
        // 아직 저장되지 않은 처방약인 경우 목록에서 제거
        newPrescriptions.splice(prescriptionIndex, 1);
        return { ...prev, prescriptions: newPrescriptions };
      }
    });
  };

  const handleAddPrescription = () => {
    setGroup((prev) => ({
      ...prev,
      prescriptions: [
        ...(prev.prescriptions || []),
        {
          prescriptionSequence: (prev.prescriptions?.length || 0) + 1,
          prescriptionDate: prev.prescriptionDate,
          hospital: prev.hospital,
        },
      ],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 품목기준코드(itemCode)가 없는 약품 확인
    const missingItemCodePrescriptions = group.prescriptions?.filter(
      (prescription) => prescription.medicationName && !prescription.itemCode
    ) || [];
    
    if (missingItemCodePrescriptions.length > 0) {
      // 경고 메시지 생성
      const drugNames = missingItemCodePrescriptions
        .map(p => `• ${p.medicationName}`)
        .join('\n');
      
      const warningMessage = 
        `다음 의약품을 다시 정확히 입력해주십시오:\n` +
        `${drugNames}\n\n` +
        `해결방법: 의약품명 입력 → 엔터 → 검색결과에서 정확한 의약품 선택\n\n` +
        `수정하지 않고 진행할 경우 허가사항이 반영되지 않습니다.\n` +
        `저장하시겠습니까?`;
      
      // window.confirm 사용하여 사용자에게 확인
      const confirmed = window.confirm(warningMessage);
      
      if (!confirmed) {
        // 사용자가 취소를 선택한 경우 저장 중단
        log(LogLevel.INFO, "[PrescriptionFormTemplate] 사용자가 저장을 취소했습니다.");
        return;
      }
      
      log(LogLevel.WARNING, "[PrescriptionFormTemplate] 품목기준코드 누락 경고 후 저장 진행:", {
        missingCount: missingItemCodePrescriptions.length,
        drugs: missingItemCodePrescriptions.map(p => p.medicationName),
      });
    }
    
    await onSubmit(group);
  };

  // [2025-01-17] 결정사항: 엑셀 업로드 선택 시 기존 onExcelUploadClick 콜백 호출 후 모드 변경
  const handleExcelUpload = () => {
    if (onExcelUploadClick) {
      onExcelUploadClick();
    }
    setInputMode("excel");
  };

  // [2025-01-17] 결정사항: 직접 입력 선택 시 빈 처방전 하나를 추가하고 직접 입력 모드로 변경
  const handleManualInput = () => {
    if (group.prescriptions?.length === 0) {
      setGroup((prev) => ({
        ...prev,
        prescriptions: [{ ...EMPTY_PRESCRIPTION }],
      }));
    }
    setInputMode("manual");
  };

  // NHIS 요청 관련 상태
  // 2스텝 구성: 'intro'(진행 안내 + 주의사항 확인) → 'input'(환자 정보 입력)
  const [nhisSubStep, setNhisSubStep] = useState<"intro" | "input">("intro");
  const [nhisIntroChecked, setNhisIntroChecked] = useState(false);
  const [nhisPatientName, setNhisPatientName] = useState("");
  const [nhisPhoneNumber, setNhisPhoneNumber] = useState("");
  const [nhisLoading, setNhisLoading] = useState(false);
  const [nhisConsentUrl, setNhisConsentUrl] = useState<string | null>(null);
  const [nhisError, setNhisError] = useState<string | null>(null);
  const [nhisTransactionId, setNhisTransactionId] = useState<string | null>(null);
  const [nhisStatus, setNhisStatus] = useState<NhisStatus>("PENDING");
  const [nhisCreatedAt, setNhisCreatedAt] = useState<string>("");

  const handleNhisRequest = () => {
    setInputMode("nhis");
  };

  const handleNhisSubmit = async () => {
    if (!nhisPatientName.trim() || !nhisPhoneNumber.trim() || !group.patientId) return;

    setNhisLoading(true);
    setNhisError(null);

    const result = await createNhisRequest({
      patientId: group.patientId,
      patientName: nhisPatientName.trim(),
      phoneNumber: nhisPhoneNumber.trim(),
    });

    if (result.error) {
      log(LogLevel.ERROR, "[PrescriptionFormTemplate] NHIS 요청 실패:", result);
      setNhisError(result.message || "건강정보 요청 생성에 실패했습니다.");
      setNhisLoading(false);
      return;
    }

    setNhisConsentUrl(result.data?.consentUrl || null);
    setNhisTransactionId(result.data?.transactionId || null);
    setNhisStatus("PENDING");
    setNhisCreatedAt(new Date().toISOString());
    setNhisLoading(false);

    // 처방내역 조회 현황 대시보드 즉시 갱신
    window.dispatchEvent(
      new CustomEvent("nhis-status-update", { detail: { patientId: group.patientId } })
    );
  };

  // NHIS 상태 실시간 갱신: FCM → nhis-status-update 이벤트 수신 시 상태 재조회
  useEffect(() => {
    if (!nhisTransactionId) return;
    const handler = async () => {
      const result = await fetchNhisRequestStatus(nhisTransactionId);
      if (!result.error && result.data) {
        setNhisStatus(result.data.status);
      }
    };
    window.addEventListener("nhis-status-update", handler);
    return () => window.removeEventListener("nhis-status-update", handler);
  }, [nhisTransactionId]);

  // [2025-01-17] 결정사항: 뒤로가기 기능으로 선택 화면으로 돌아가기
  const handleBackToSelection = () => {
    setInputMode("selection");
  };

  // [2025-01-02] 식별약 선택 핸들러
  const handleIdentifiedDrugSelect = async (selectedItemCode: string) => {
    try {
      // itemCode로 약품 상세 정보 조회
      const response = await fetchDrugs({ itemCode: selectedItemCode, limit: 1, offset: 0 });
      const drugInfo = response.data?.[0];

      if (!drugInfo) {
        ToastManager.show({ content: "약품 정보를 찾을 수 없습니다." });
        return;
      }

      // 새 행 추가 및 약품 정보 채우기
      const newPrescriptions = [...(group.prescriptions || []), { ...EMPTY_PRESCRIPTION }];
      const newIndex = newPrescriptions.length - 1;

      newPrescriptions[newIndex] = {
        ...EMPTY_PRESCRIPTION,
        itemCode: drugInfo.itemCode,
        standardCode: drugInfo.standardCode,
        insuranceCode: drugInfo.insuranceCode,
        medicationName: drugInfo.productName,
        dosageUnit: drugInfo.dosageUnit,
        source: "identification",
      };

      setGroup(prev => ({ ...prev, prescriptions: newPrescriptions }));

      // 새로 추가된 행의 투약량 필드로 포커스 이동
      setTimeout(() => {
        const dosageInput = document.getElementById(`dosage-input-${newIndex}`);
        if (dosageInput) dosageInput.focus();
      }, 100);
    } catch (error) {
      log(LogLevel.ERROR, "[PrescriptionFormTemplate] 식별약 추가 실패:", error);
      ToastManager.show({ content: "약품 정보 조회에 실패했습니다." });
    }
  };

  // [2025-01-17] 결정사항: 선택 화면 렌더링
  const renderSelectionScreen = () => (
    <FormContainer>
      <Section>
        <SectionHeader>
          <Text textType="LargeTitle2">{title}</Text>
        </SectionHeader>

        <SelectionContainer>
          <Text
            textType="Body1"
            color={COLOR_SYSTEM.get("Gray")?.[600]}
            style={{ textAlign: "center" }}
          >
            처방전 등록 방법을 선택해주세요
          </Text>

          <SelectionOptions>
            {isNhisEnabled && (
              <SelectionCard onClick={handleNhisRequest}>
                <IconWrapper>
                  <Icon name="Mobile Filled" size={32} color={COLOR_SYSTEM.get("Blue")?.[500]} />
                </IconWrapper>
                <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[800]}>
                  처방내역 가져오기
                </Text>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ textAlign: "left", alignSelf: "stretch" }}>
                  환자가 카카오톡 등으로 본인인증을 하면 심평원 '내가먹는약 한눈에' 처방내역이 자동으로 불러와집니다.
                </Text>
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]} style={{ textAlign: "left", alignSelf: "stretch" }}>
                  1. 환자 이름/전화번호 입력
                  <br />
                  2. 환자에게 알림톡 전송됨
                  <br />
                  3. 환자가 본인의 핸드폰으로 간편인증
                  <br />
                  4. PhAI 사이트에 환자의 처방이력이 등록됩니다
                </Text>
              </SelectionCard>
            )}

            <SelectionCard onClick={handleExcelUpload}>
              <IconWrapper>
                <Icon name="Upload" size={32} color={COLOR_SYSTEM.get("Blue")?.[500]} />
              </IconWrapper>
              <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[800]}>
                엑셀 파일로 등록하기
              </Text>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ textAlign: "left", alignSelf: "stretch" }}>
                약사님이 공단에서 받으신 엑셀이나
                <br />
                직접 작성하신 엑셀 파일을 업로드합니다.
              </Text>
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]} style={{ textAlign: "left", alignSelf: "stretch" }}>
                <strong>가능한 파일</strong>
                <br />
                • 다제약물관리 시범사업 공단 다운로드 엑셀
                <br />
                • '내가먹는약 한눈에' 다운로드 엑셀
                <br />
                • 직접 작성한 엑셀
              </Text>
            </SelectionCard>

            <SelectionCard onClick={handleManualInput}>
              <IconWrapper>
                <Icon name="Edit 01" size={32} color={COLOR_SYSTEM.get("Blue")?.[500]} />
              </IconWrapper>
              <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[800]}>
                직접 입력하기
              </Text>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ textAlign: "left", alignSelf: "stretch" }}>
                약품명이나 보험코드로 검색하여
                <br />
                한 건씩 직접 입력합니다.
              </Text>
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]} style={{ textAlign: "left", alignSelf: "stretch" }}>
                • 보험코드 또는 의약품명으로 검색
                <br />
                • 투약량, 횟수, 일수 직접 입력
                <br />
                • 처방전 단위별로 입력
              </Text>
            </SelectionCard>
          </SelectionOptions>
        </SelectionContainer>

        <ButtonContainer>
          <Button
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={onCancel}
          >
            취소
          </Button>
        </ButtonContainer>
      </Section>
    </FormContainer>
  );

  // [2025-01-17] 결정사항: 직접 입력 화면 렌더링 (기존 코드 활용)
  const renderManualInputScreen = () => (
    <FormContainer>
      <ContentWrapper $showPanel={showIdentifiedDrugs}>
        <MainContent>
          <Section>
            {!isEditMode && (
              <BackButton onClick={handleBackToSelection}>
                <Icon name="Arrow Left" size={16} color={COLOR_SYSTEM.get("Gray")?.[600]} />
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  등록 방법 다시 선택
                </Text>
              </BackButton>
            )}

            <SectionHeader>
              <Text textType="LargeTitle2">{isEditMode ? title : `${title} - 직접입력`}</Text>
            </SectionHeader>

            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                  <div>
                    <Text textType="Body2" color="var(--gray-700)" style={{ marginBottom: "4px" }}>
                      병원명(선택사항)
                    </Text>
                    <Input
                      placeholder="예: 류마티스내과"
                      value={group.hospital}
                      onChange={(e) => setGroup((prev) => ({ ...prev, hospital: e.target.value }))}
                      style={{ maxWidth: "300px" }}
                      list="hospital-name-list"
                    />
                    {existingHospitalNames && existingHospitalNames.length > 0 && (
                      <datalist id="hospital-name-list">
                        {existingHospitalNames.map((name) => (
                          <option key={name} value={name} />
                        ))}
                      </datalist>
                    )}
                  </div>
                  <div>
                    <Text textType="Body2" color="var(--gray-700)" style={{ marginBottom: "4px" }}>
                      처방일자(선택사항)
                    </Text>
                    <Input
                      type="date"
                      value={
                        group.prescriptionDate
                          ? getDateStringFromToday({ date: group.prescriptionDate })
                          : null
                      }
                      onChange={(e) =>
                        setGroup((prev) => ({ ...prev, prescriptionDate: new Date(e.target.value) }))
                      }
                      style={{ maxWidth: "200px" }}
                    />
                  </div>
                </div>
                {/* [2025-01-02] 식별약 토글 버튼 - 패널이 닫혀있을 때만 표시 (열려있으면 패널 내 닫기 버튼 사용) */}
                {!showIdentifiedDrugs && (
                  <Button
                    buttonType="button"
                    color="Secondary"
                    size="Small"
                    type="Icon"
                    variant="Outlined"
                    icon="Chevron Right"
                    onClick={() => setShowIdentifiedDrugs(true)}
                    title="내가 식별한 약에서 추가하기"
                  />
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                <Icon name="Information Circle" size={14} color={COLOR_SYSTEM.get("Gray")?.[300]} />
                <Text textType="InputSmall" color={COLOR_SYSTEM.get("Gray")?.[300]}>
                  같은 병원은 동일한 이름으로 입력해야 AI가 같은 병원으로 인식합니다
                </Text>
              </div>
            </div>

            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: COLOR_SYSTEM.get("Blue")?.[50],
                border: `1px solid ${COLOR_SYSTEM.get("Blue")?.[200]}`,
                borderRadius: "8px",
              }}
            >
              <Text
                textType="Body2"
                color={COLOR_SYSTEM.get("Blue")?.[700]}
                style={{ fontWeight: 600, marginBottom: "4px" }}
              >
                🔍 처방의약품 검색 방법
                <br />
              </Text>
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                • 보험코드나 의약품명을 입력한 후 엔터를 눌러 검색하세요
                <br />
                • 보험코드 검색 시 의약품명과 품목기준코드가 자동으로 입력됩니다
                <br />• 투약량, 횟수, 일수, 용법은 직접 입력해주세요
              </Text>
            </div>

            <PrescriptionCard>
          <MedicineTable>
            <thead>
              <TableHeader>
                <td
                  className="sequence-number"
                  style={{ width: "25px", padding: "4px 2px", textAlign: "center" }}
                ></td>
                <td className="standard-code">
                  <Text textType="Caption" color="var(--gray-700)">
                    보험코드 또는 표준코드
                  </Text>
                </td>
                <td className="medication-name">
                  <Text textType="Caption" color="var(--gray-700)">
                    의약품명
                  </Text>
                </td>
                <td className="dosage">
                  <Text textType="Caption" color="var(--gray-700)">
                    투약량
                  </Text>
                </td>
                <td className="frequency">
                  <Text textType="Caption" color="var(--gray-700)">
                    투약횟수
                  </Text>
                </td>
                <td className="duration">
                  <Text textType="Caption" color="var(--gray-700)">
                    투약일수
                  </Text>
                </td>
                <td className="usage-instruction">
                  <Text textType="Caption" color="var(--gray-700)">
                    용법
                  </Text>
                </td>
                <td className="item-code">
                  <Text textType="Caption" color="var(--gray-700)">
                    품목기준코드{" "}
                    <span
                      style={{
                        fontSize: "10px",
                        backgroundColor: COLOR_SYSTEM.get("Gray")?.[100],
                        color: COLOR_SYSTEM.get("Gray")?.[600],
                        padding: "1px 4px",
                        borderRadius: "3px",
                        marginLeft: "4px",
                      }}
                    >
                      자동입력
                    </span>
                  </Text>
                </td>
                <td className="delete-button"></td>
              </TableHeader>
            </thead>
            <tbody>
              {group.prescriptions?.map((prescription, prescriptionIndex) => {
                log(LogLevel.VERBOSE, "group.prescription", prescription);
                return (
                  <TableRow key={prescriptionIndex}>
                    <td
                      className="sequence-number"
                      style={{
                        width: "25px",
                        padding: "4px 2px",
                        textAlign: "center",
                        fontSize: "11px",
                        fontWeight: 500,
                        color: COLOR_SYSTEM.get("Gray")?.[500],
                      }}
                    >
                      {prescriptionIndex + 1}
                    </td>
                    <td className="standard-code">
                      <DrugSearchInput
                        id={`standard-code-input-${prescriptionIndex}`}
                        value={prescription.standardCode}
                        onChange={(value) => {
                          log(LogLevel.VERBOSE, "DrugSearchInput: onChange", value);
                          handleMedicineChange(prescriptionIndex, 0, "standardCode", value);
                        }}
                        onSelect={(drug) => {
                          log(LogLevel.INFO, "DrugSearchInput: onSelect", drug);
                          handleDrugSelect(prescriptionIndex, drug);
                        }}
                        placeholder="보험코드로 검색"
                        autoSearchOnNineDigits={true}
                        itemCode={prescription.itemCode}
                        onEmptyEnter={() => {
                          // 보험코드를 모르면 엔터로 의약품명 검색으로 넘어간다
                          document
                            .getElementById(`medication-name-input-${prescriptionIndex}`)
                            ?.focus();
                        }}
                      />
                    </td>
                    <td className="medication-name">
                      <DrugSearchInput
                        id={`medication-name-input-${prescriptionIndex}`}
                        placeholder="의약품명으로 검색"
                        value={prescription.medicationName}
                        onChange={(value) =>
                          handleMedicineChange(prescriptionIndex, 0, "medicationName", value)
                        }
                        onSelect={(drug) => {
                          log(LogLevel.INFO, "DrugSearchInput: onSelect", drug);
                          handleDrugSelect(prescriptionIndex, drug);
                        }}
                        type="productName"
                        itemCode={prescription.itemCode}
                      />
                    </td>
                    <td className="dosage">
                      <Input
                        id={`dosage-input-${prescriptionIndex}`}
                        type="number"
                        placeholder=""
                        value={prescription.dosagePerAdministration?.toString()}
                        onChange={(e) =>
                          handleMedicineChange(
                            prescriptionIndex,
                            0,
                            "dosagePerAdministration",
                            e.target.value
                          )
                        }
                        onEnter={() => handleEnterKeyPress(prescriptionIndex, 'dosage')}
                        rightItems={prescription.dosageUnit ? [prescription.dosageUnit] : []}
                      />
                    </td>
                    <td className="frequency">
                      <Input
                        id={`frequency-input-${prescriptionIndex}`}
                        type="number"
                        placeholder=""
                        value={prescription.timesPerDay?.toString()}
                        onChange={(e) =>
                          handleMedicineChange(prescriptionIndex, 0, "timesPerDay", e.target.value)
                        }
                        onEnter={() => handleEnterKeyPress(prescriptionIndex, 'frequency')}
                        rightItems={["회"]}
                      />
                    </td>
                    <td className="duration">
                      <Input
                        id={`duration-input-${prescriptionIndex}`}
                        type="number"
                        placeholder=""
                        value={prescription.daysOfAdministration?.toString()}
                        onChange={(e) =>
                          handleMedicineChange(
                            prescriptionIndex,
                            0,
                            "daysOfAdministration",
                            e.target.value
                          )
                        }
                        onEnter={() => handleEnterKeyPress(prescriptionIndex, 'duration')}
                        rightItems={["일"]}
                      />
                    </td>
                    <td className="usage-instruction">
                      <Input
                        id={`usage-input-${prescriptionIndex}`}
                        placeholder="예: 저녁에"
                        value={prescription.usageInstruction}
                        onChange={(e) =>
                          handleMedicineChange(
                            prescriptionIndex,
                            0,
                            "usageInstruction",
                            e.target.value
                          )
                        }
                        onEnter={() => handleEnterKeyPress(prescriptionIndex, 'usage')}
                      />
                    </td>
                    <td className="item-code">
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", position: "relative" }}>
                        <Input
                          placeholder="(자동입력)"
                          value={prescription.itemCode}
                          onChange={(e) =>
                            handleMedicineChange(prescriptionIndex, 0, "itemCode", e.target.value)
                          }
                          disabled={true}
                          disabledColor={false}
                          backgroundColor={COLOR_SYSTEM.get("Gray")?.[100]}
                          style={{
                            flex: 1,
                            opacity: 0.8,
                            pointerEvents: 'none'
                          }}
                        />
                        {(prescription.medicationName || prescription.standardCode) && !prescription.itemCode && (
                          <div
                            style={{
                              position: "absolute",
                              right: "8px",
                              display: "flex",
                              alignItems: "center",
                              pointerEvents: "auto",
                              cursor: "default",
                              zIndex: 1
                            }}
                          >
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                              <Icon
                                name="Exclamation Mark Circle"
                                size={16}
                                color={COLOR_SYSTEM.get("Red")?.[500]}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "100%",
                                  right: "-10px",
                                  marginBottom: "8px",
                                  padding: "8px 12px",
                                  backgroundColor: COLOR_SYSTEM.get("Gray")?.[800],
                                  color: "white",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  opacity: 0,
                                  pointerEvents: "none",
                                  transition: "opacity 0.2s",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                                }}
                                className="tooltip"
                              >
                                의약품을 다시 검색해주세요
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    right: "14px",
                                    width: 0,
                                    height: 0,
                                    borderLeft: "4px solid transparent",
                                    borderRight: "4px solid transparent",
                                    borderTop: `4px solid ${COLOR_SYSTEM.get("Gray")?.[800]}`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="delete-button">
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                          buttonType="button"
                          color="Negative"
                          size="Small"
                          type="Icon"
                          variant="Ghost"
                          icon="Trash"
                          onClick={() => handleRemoveMedicine(prescriptionIndex)}
                        />
                      </div>{" "}
                    </td>
                  </TableRow>
                );
              })}
            </tbody>
          </MedicineTable>

          <div ref={addMedicineButtonRef} style={{ display: "inline-block", marginTop: "12px" }}>
            <Button
              buttonType="button"
              color="Primary"
              size="Small"
              type="IconWithText"
              variant="Ghost"
              left
              icon="Plus"
              onClick={() => handleAddMedicine()}
            >
              약물 추가
            </Button>
          </div>
            </PrescriptionCard>

            <ButtonContainer>
              <Button
                buttonType="button"
                color="Tertiary"
                size="Medium"
                type="Text"
                variant="Outlined"
                onClick={onCancel}
              >
                취소
              </Button>
              <Button
                buttonType="submit"
                color="Primary"
                size="Medium"
                type="IconWithText"
                variant="Contained"
                icon="Check"
                left
                onClick={handleSubmit}
              >
                저장
              </Button>
            </ButtonContainer>
          </Section>
        </MainContent>

        {/* [2025-01-02] 식별약 패널 */}
        {showIdentifiedDrugs && (
          <SidePanel>
            <IdentifiedDrugList
              onDrugSelect={handleIdentifiedDrugSelect}
              onClose={() => setShowIdentifiedDrugs(false)}
            />
          </SidePanel>
        )}
      </ContentWrapper>
    </FormContainer>
  );

  // [2025-01-17] 결정사항: 엑셀 업로드 화면 렌더링 (기존 엑셀 업로드 UI 활용)
  const renderExcelUploadScreen = () => (
    <FormContainer>
      <Section>
        <BackButton onClick={handleBackToSelection}>
          <Icon name="Arrow Left" size={16} color={COLOR_SYSTEM.get("Gray")?.[600]} />
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
            등록 방법 다시 선택
          </Text>
        </BackButton>

        <SectionHeader>
          <Text textType="LargeTitle2">{title} - 엑셀 업로드</Text>
        </SectionHeader>

        <UploadButtons>
          <Button
            buttonType="button"
            variant="Outlined"
            color="Tertiary"
            size="Medium"
            type="IconWithText"
            icon="Upload"
            left
            onClick={onExcelUploadClick}
          >
            엑셀 업로드
          </Button>
        </UploadButtons>
        <div style={{ marginTop: "8px", marginBottom: "16px" }}>
          <Text
            textType="Caption"
            color={COLOR_SYSTEM.get("Gray")?.[500]}
            style={{ display: "block" }}
          >
            공단에서 다운받은 엑셀 또는 직접 엑셀에 작성하여 업로드하세요
          </Text>
          <a href="/downloads/PrescriptionTemplate.xlsx" download="처방전_엑셀양식.xlsx" style={{ textDecoration: "none", display: "block", marginTop: "4px" }}>
            <Text textType="Caption" color={COLOR_SYSTEM.get("Skyblue")?.[500]}>
              엑셀 양식 다운로드
            </Text>
          </a>
        </div>

        <ButtonContainer>
          <Button
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={onCancel}
          >
            취소
          </Button>
        </ButtonContainer>
      </Section>
    </FormContainer>
  );

  // NHIS 요청 화면 렌더링
  const renderNhisScreen = () => (
    <FormContainer>
      <Section>
        {!nhisConsentUrl ? (
          nhisSubStep === "intro" ? (
            // === 1단계: 진행 안내 + 주의사항 확인 ===
            <>
              <BackButton onClick={handleBackToSelection}>
                <Icon name="Arrow Left" size={16} color={COLOR_SYSTEM.get("Gray")?.[600]} />
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  등록 방법 다시 선택
                </Text>
              </BackButton>

              <SectionHeader>
                <Text textType="LargeTitle2">처방내역 가져오기</Text>
              </SectionHeader>

              {/* 경고 박스 — 세로 체크리스트 */}
              <div
                style={{
                  padding: "28px 32px",
                  backgroundColor: COLOR_SYSTEM.get("Orange")?.[50],
                  border: `1.5px solid ${COLOR_SYSTEM.get("Orange")?.[200]}`,
                  borderRadius: "16px",
                  marginBottom: "28px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "24px",
                  }}
                >
                  <Icon
                    name="Exclamation Mark Polygon"
                    size={24}
                    color={COLOR_SYSTEM.get("Orange")?.[600]}
                    style={{ flexShrink: 0 }}
                  />
                  <Text
                    textType="H3"
                    color={COLOR_SYSTEM.get("Orange")?.[700]}
                    style={{ fontWeight: 700, fontSize: "20px" }}
                  >
                    링크를 보내기 전에 확인해 주세요
                  </Text>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                  {/* 항목 1: 전화 */}
                  <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: COLOR_SYSTEM.get("Orange")?.[100],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: "2px",
                      }}
                    >
                      <Icon name="Call" size={18} color={COLOR_SYSTEM.get("Orange")?.[600]} />
                    </div>
                    <div>
                      <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[900]} style={{ lineHeight: "1.5", display: "block", marginBottom: "2px" }}>
                        먼저 환자분께 전화로 안내해 주셨나요?
                      </Text>
                      <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]} style={{ lineHeight: "1.5" }}>
                        PhAI는 공단·약사회의 공식 시스템이 아니므로,<br />사전 안내 없이 링크만 보내면 스미싱으로 오해할 수 있습니다.
                      </Text>
                    </div>
                  </div>

                  {/* 항목 2: 정보 확인 */}
                  <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: COLOR_SYSTEM.get("Orange")?.[100],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: "2px",
                      }}
                    >
                      <Icon name="Edit 01" size={18} color={COLOR_SYSTEM.get("Orange")?.[600]} />
                    </div>
                    <div>
                      <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[900]} style={{ lineHeight: "1.5", display: "block", marginBottom: "2px" }}>
                        이름과 전화번호가 정확한지 확인해 주세요.
                      </Text>
                      <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]} style={{ lineHeight: "1.5" }}>
                        정보가 틀리면 환자의 본인인증이 실패합니다.
                      </Text>
                    </div>
                  </div>

                  {/* 항목 3: 만료 */}
                  <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: COLOR_SYSTEM.get("Orange")?.[100],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: "2px",
                      }}
                    >
                      <Icon name="Clock" size={18} color={COLOR_SYSTEM.get("Orange")?.[600]} />
                    </div>
                    <div>
                      <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[900]} style={{ lineHeight: "1.5", display: "block", marginBottom: "2px" }}>
                        발송된 링크는 24시간 후에 만료됩니다.
                      </Text>
                      <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]} style={{ lineHeight: "1.5" }}>
                        기간 내 완료를 독려해 주세요.
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              {/* 체크박스 */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "16px",
                  backgroundColor: nhisIntroChecked ? COLOR_SYSTEM.get("Blue")?.[50] : COLOR_SYSTEM.get("Gray")?.[50],
                  border: `1px solid ${nhisIntroChecked ? COLOR_SYSTEM.get("Blue")?.[200] : COLOR_SYSTEM.get("Gray")?.[200]}`,
                  borderRadius: "8px",
                  marginBottom: "24px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  type="checkbox"
                  checked={nhisIntroChecked}
                  onChange={(e) => setNhisIntroChecked(e.target.checked)}
                  style={{ width: "18px", height: "18px", accentColor: COLOR_SYSTEM.get("Blue")?.[500], cursor: "pointer" }}
                />
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[800]}>
                  환자분께 서비스 안내와 링크 발송에 대해 미리 설명하였습니다.
                </Text>
              </label>

              <ButtonContainer>
                <Button
                  buttonType="button"
                  color="Tertiary"
                  size="Medium"
                  type="Text"
                  variant="Outlined"
                  onClick={onCancel}
                >
                  취소
                </Button>
                <Button
                  buttonType="button"
                  color="Primary"
                  size="Medium"
                  type="Text"
                  variant="Contained"
                  onClick={() => setNhisSubStep("input")}
                  disabled={!nhisIntroChecked}
                  disabledColor={!nhisIntroChecked}
                >
                  환자 정보 입력하기
                </Button>
              </ButtonContainer>
            </>
          ) : (
            // === 2단계: 환자 정보 입력 ===
            <>
              <BackButton onClick={() => { setNhisIntroChecked(false); setNhisSubStep("intro"); }}>
                <Icon name="Arrow Left" size={16} color={COLOR_SYSTEM.get("Gray")?.[600]} />
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  안내 다시 보기
                </Text>
              </BackButton>

              <div style={{ maxWidth: "400px" }}>
              <SectionHeader>
                <Text textType="LargeTitle2">환자 정보 입력</Text>
              </SectionHeader>

              <div
                style={{
                  padding: "12px 16px",
                  backgroundColor: COLOR_SYSTEM.get("Orange")?.[50],
                  border: `1px solid ${COLOR_SYSTEM.get("Orange")?.[200]}`,
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <Text textType="Body2" color={COLOR_SYSTEM.get("Orange")?.[700]} style={{ lineHeight: "1.6" }}>
                  환자 본인의 이름과 전화번호를 정확히 입력해주세요.
                  <br />
                  정보가 틀리면 환자의 본인인증이 실패합니다.
                </Text>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ marginBottom: "4px" }}>
                  환자 이름
                </Text>
                <Input
                  placeholder="홍길동"
                  value={nhisPatientName}
                  onChange={(e) => setNhisPatientName(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: "24px" }}>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ marginBottom: "4px" }}>
                  환자 전화번호
                </Text>
                <Input
                  placeholder="01012345678"
                  value={nhisPhoneNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setNhisPhoneNumber(val);
                  }}
                />
              </div>

              {nhisError && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: COLOR_SYSTEM.get("Red")?.[50],
                    border: `1px solid ${COLOR_SYSTEM.get("Red")?.[200]}`,
                    borderRadius: "8px",
                    marginBottom: "16px",
                  }}
                >
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Red")?.[600]}>
                    {nhisError}
                  </Text>
                </div>
              )}

              <ButtonContainer style={{ justifyContent: "flex-start" }}>
                <Button
                  buttonType="button"
                  color="Tertiary"
                  size="Medium"
                  type="Text"
                  variant="Outlined"
                  onClick={onCancel}
                  style={{ flex: "0 0 auto" }}
                >
                  취소
                </Button>
                <Button
                  buttonType="button"
                  color="Primary"
                  size="Medium"
                  type="Text"
                  variant="Contained"
                  onClick={handleNhisSubmit}
                  disabled={!nhisPatientName.trim() || !nhisPhoneNumber.trim() || nhisLoading}
                  style={{ flex: "1 1 auto" }}
                >
                  {nhisLoading ? "요청 중..." : "환자에게 인증카톡 보내기"}
                </Button>
              </ButtonContainer>
              </div>
            </>
          )
        ) : (
          <>
            <SectionHeader>
              <Text textType="LargeTitle2">조회 요청 완료</Text>
            </SectionHeader>

            <NhisProgressDashboard
              status={nhisStatus}
              patientName={nhisPatientName}
              createdAt={nhisCreatedAt}
              consentUrl={nhisConsentUrl || undefined}
              onClose={onCancel}
              showStatusHeader={false}
            >
              <NhisStepPreview />
            </NhisProgressDashboard>
          </>
        )}
      </Section>
    </FormContainer>
  );

  // [2025-01-17] 결정사항: 현재 inputMode에 따라 적절한 화면을 렌더링
  switch (inputMode) {
    case "selection":
      return renderSelectionScreen();
    case "excel":
      return renderExcelUploadScreen();
    case "manual":
      return renderManualInputScreen();
    case "nhis":
      return renderNhisScreen();
    default:
      return renderSelectionScreen();
  }
};

export default PrescriptionFormTemplate;
